import { TextField, Autocomplete, Box, Typography } from '@mui/material';
import { BankDetailsDto } from '@sr/dto';
import { TextFieldProps, fieldToTextField } from 'formik-mui';
import { SyntheticEvent, useCallback, useState } from 'react';
import { IRemoteCollection } from '../../../utils/remote-collection.hook';

export const BankDetailsPicker = (
  props: TextFieldProps & {
    legalEntityId?: number;
    collection: IRemoteCollection<BankDetailsDto>;
    /** дизейблить реквизиты, привязанные к заблокированному для исходящих счетов расчётному счёту */
    disableBlockedForOutgoing?: boolean;
  },
) => {
  const {
    form: { setFieldValue },
    field: { name, value },
    label,
    disabled,
    legalEntityId,
    collection,
    disableBlockedForOutgoing,
  } = props;

  const isOptionBlocked = (option: BankDetailsDto) =>
    !!disableBlockedForOutgoing && option.outgoingInvoicesAllowed === false;

  const [open, setOpen] = useState(false);

  const onChange = useCallback(
    async (_event: SyntheticEvent<Element, Event>, newValue: BankDetailsDto | null) => {
      await setFieldValue(name, newValue?.id);
    },
    [setFieldValue, name]
  );

  const loading = open && collection?.items.length === 0 && collection?.isLoading;
  if (loading) {
    return null;
  }

  const hasLegalEntityId = legalEntityId !== undefined && !isNaN(legalEntityId);
  const options = hasLegalEntityId ? collection?.items.filter(item => item.legalEntityId === legalEntityId) || [] : [];
  const selectedEntity = options.find(item => item.id === value) ?? null;

  const {
    legalEntityId: _,
    disableBlockedForOutgoing: __,
    ...textFieldProps } = props;

  return (
    <Autocomplete
      value={selectedEntity}
      autoComplete={false}
      options={options}
      open={open}
      disabled={disabled}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      onChange={onChange}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionDisabled={isOptionBlocked}
      getOptionLabel={(option) => `${option.bankName}, р/с ${option.accountNumber} (БИК ${option.bik})`}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box>
            <Typography variant="body2">р/с {option.accountNumber}</Typography>
            <Typography variant="caption" color="text.secondary">
              {option.bankName} · БИК {option.bik} · к/с {option.corrAccountNumber}
            </Typography>
            {isOptionBlocked(option) && (
              <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                Расчётный счёт запрещён для исходящих счетов
              </Typography>
            )}
          </Box>
        </Box>
      )}
      loading={loading}
      loadingText={'Загрузка...'}
      noOptionsText={hasLegalEntityId ? 'Ничего не найдено' : 'Укажите юрлицо'}
      renderInput={
        (params) => <TextField
          {...fieldToTextField(textFieldProps)}
          {...params}
          name={name}
          label={label}
          placeholder="Начните набирать..." />
      }
    />);
};
